<template>
    <div id="page" :class="['page', movementCursor, isLocked]">
        <main class="page__content" :style="[styleFontFamily, stylePreviewDevice]">
            <img v-if="previewDevice" class="page__device" :src="require(`@/assets/preview/${ deviceMode.deviceImage }`)">

            <div ref="content" class="page__content-container" :style="stylePreviewContent">
                <transition-group v-if="renderings.length" name="app-components" tag="div">
                    <div v-for="(rendering, index) in renderings" :key="rendering.id" class="fc-display-contents">
                        <droppable
                            v-if="isTemplateScreen"
                            force-display
                            :index="index"
                            scroll-on-drop
                            element-type="droppable"
                            @drop="data => onDrop(data, index)"
                            @click.native.right.prevent.stop="event => openPasteMenu(event, index)"
                            @paste="pasteElement"
                        />

                        <component
                            :is="rendering.elementType"
                            :ref="`${ rendering.elementType }_${ index }`"
                            :pointer="index + 1"
                            :index="index"
                            :screen="screen"
                            :page-id="pageId"
                            :top-level="true"
                            :root-level="true"
                            v-bind="rendering"
                            @element-updated="updateRendering"
                            @click.native.right.stop.prevent="event => openCopyMenu(event, rendering)"
                            @cut="onCut({ element: rendering, onCutCallback: deleteElement })"
                            @copy="onCopy({ element: rendering })"
                            @delete-element="deleteElementByIndex"
                            @move-element="moveElement"
                        />
                    </div>
                </transition-group>

                <droppable
                    v-if="isTemplateScreen"
                    force-display
                    :index="renderings.length"
                    element-type="droppable"
                    scroll-on-drop
                    @drop="data => onDrop(data, renderings.length)"
                    @click.native.right.prevent.stop="event => openPasteMenu(event, renderings.length)"
                    @paste="pasteElement"
                />
            </div>
        </main>
    </div>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex';
import uuidv4 from 'uuid/v4';

import { SET_RENDERINGS, SET_CHANGED_DATA, SET_PAGE_DATA, SET_LAST_PAGE_ID, SET_HOLD_EVENTS, MOVE_RENDERING, HIDE_DRAWER } from '@/store/types/mutation-types';
import { LOAD_PAGE, LOAD_PRESETS } from '@/store/types/action-types';
import { tailwindConfig } from '@/data/tailwind';
import cutCopyPasteMixin from '@/mixins/cut-copy-paste';
import listenOnRootMixin from '@/mixins/listen-on-root';
import flexDeepCopy from '@/utils/flex-deep-copy';
import { SCREEN_NAMES } from '@/data/constants/screen';
import { generateRendering } from '@/utils/rendering';
import { STATE_CLASSES } from '@/data/constants/classes';

export default {
    name: 'page',
    mixins: [cutCopyPasteMixin, listenOnRootMixin],

    props: {
        pageId: {
            type: String,
            default: '',
        },

        screen: {
            type: String,
            required: true,
        },
    },

    data() {
        return {
            checked: true,
        };
    },

    computed: {
        ...mapState({
            copiedElements: state => state.page.copiedElements,
            deviceMode: state => state.page.deviceMode,
            onCutCallback: state => state.page.onCutCallback,
            pageData: state => state.page.pageData,
            pageDataComparison: state => state.page.pageDataComparison,
            previewMode: state => state.previewMode,
            renderings: state => state.page.renderings,
            selectedElements: state => state.page.selectedElements,
            componentSaved: state => state.page.componentSaved,
            user: state => state.user.user,
            username: state => state.user.username,
        }),

        isLocked() {
            if (this.user && this.user.admin) {
                return '';
            }

            if (
                this.pageData
                && this.username
                && this.pageData.pageData.locked
                && this.pageData.pageData.lockedby !== this.username
            ) {
                return STATE_CLASSES.LOCKED;
            }

            return '';
        },

        movementCursor() {
            if (!this.copiedElements.elements.length) {
                return '';
            }

            if (this.copiedElements.mode === 'cut') {
                return STATE_CLASSES.CUTTING;
            }

            if (this.copiedElements.mode === 'copy') {
                return STATE_CLASSES.COPYING;
            }

            return '';
        },

        previewDevice() {
            return this.previewMode && (this.deviceMode.name === 'mob' || this.deviceMode.name === 'tab');
        },

        styleFontFamily() {
            return { fontFamily: tailwindConfig.theme.fontFamily.default };
        },

        stylePreviewDevice() {
            return {
                'max-width': this.deviceMode.deviceWidth,
                'max-height': this.deviceMode.deviceHeight,
            };
        },

        stylePreviewContent() {
            return {
                'max-width': this.deviceMode.deviceContentWidth,
                'max-height': this.deviceMode.deviceContentHeight,
            };
        },

        isTemplateScreen() {
            return this.screen === SCREEN_NAMES.TEMPLATE_EDITOR;
        },
    },

    watch: {
        async pageData(val) {
            if (val && val.layout) {
                // caching issue during language switch
                // requires clearing renderings
                this.setRenderings([]);

                await this.$nextTick();
                this.setHoldEvents(true);
                this.setRenderings(val.layout.placeholders.main);
            }
        },

        pageId(val) {
            if (val) {
                const lang = this.getLanguage();
                this.setLoad(lang);
                this.hideDrawer();
            }
        },
    },

    beforeMount() {
        if (this.pageId) {
            // watch on pageData sets renderings
            // maybe set a loader on page to do this more elegantly
            const lang = this.getLanguage();
            this.setLoad(lang);
        }

        this.listenOnRoot('flex::page::load', this.setLoad);
    },

    created() {
        this.loadPresets();
    },

    methods: {
        ...mapActions({
            loadPage: LOAD_PAGE,
            loadPresets: LOAD_PRESETS,
        }),

        ...mapMutations({
            hideDrawer: HIDE_DRAWER,
            moveRendering: MOVE_RENDERING,
            setRenderings: SET_RENDERINGS,
            setChangedData: SET_CHANGED_DATA,
            setPageData: SET_PAGE_DATA,
            setLastPageId: SET_LAST_PAGE_ID,
            setHoldEvents: SET_HOLD_EVENTS,
        }),

        moveElement(direction, index) {
            let to = index;
            if (direction === 'up') {
                to = index - 1;
            } else {
                to = index + 1;
            }
            if (to < 0 || to === this.renderings.length) return;
            const element = this.renderings[index];
            this.moveRendering({ index, to, element });
            this.setChangedData(true);
        },

        addRendering(rendering, index) {
            let insertIndex = index;

            if (insertIndex > this.renderings.length) {
                insertIndex = this.renderings.length;
            }

            this.renderings.splice(insertIndex, 0, rendering);

            for (let i = insertIndex + 1; i < this.renderings.length; i++) {
                this.renderings[i].pointer++;
            }

            this.setChangedData(true);
        },

        updateRendering(updatedRendering) {
            const index = this.renderings.findIndex(r => r.id === updatedRendering.id);

            if (index === -1) {
                console.error(
                    `Main Page: could not match ID of updated rendering
                    ${ updatedRendering.name } to existing element.`,
                    updatedRendering,
                );
            }

            this.renderings.splice(index, 1, updatedRendering);
        },

        async onDrop(data, index) {
            const rendering = await generateRendering(data, index);

            this.addRendering(rendering, index);
        },

        openCopyMenu(event, element) {
            if (!this.selectedElements.length) return;

            this.$root.$emit('flex::context::open', {
                event,
                id: 'cut-copy-context-menu',
                data: {
                    element,
                    onCutCallback: this.deleteElement,
                },
            });
        },

        openPasteMenu(event, index) {
            this.$root.$emit('flex::context::open', {
                event,
                id: 'paste-context-menu',
                data: {
                    index,
                    onPasteCallback: this.pasteElement,
                },
            });
        },

        async pasteElement(index) {
            const { elements, mode } = this.copiedElements;
            let targetIndex = index;
            let firstPastedElement = null;

            if (mode === 'cut') this.onCutCallback();

            elements.forEach((elem, i) => {
                const objClone = mode === 'copy' ? flexDeepCopy(elem) : { ...elem };

                objClone.id = uuidv4();
                this.addRendering(objClone, targetIndex);
                targetIndex++;

                if (i === 0) firstPastedElement = objClone;
            });

            this.afterPaste(firstPastedElement);
        },

        deleteElement(element) {
            const elementId = this.renderings.findIndex(r => r.id === element.id);

            this.deleteElementByIndex(elementId);
        },

        deleteElementByIndex(index) {
            if (index > -1) this.renderings.splice(index, 1);

            this.setChangedData(true);
        },

        setLoad(lang) {
            if (lang) {
                this.loadPage({ pageId: this.pageId, lang, loading: true });
            } else {
                this.loadPage({ pageId: this.pageId, loading: true });
            }
            if (this.$route.name !== 'templates') {
                this.setLastPageId(this.pageId);
            }
        },

        getLanguage() {
            let lang = this.$route.query.lang;
            if (this.pageData && this.pageData.pageData.language !== 'en') {
                lang = this.pageData.pageData.language;
            }
            return lang;
        },
    },
};
</script>

<style lang="scss">
.page {
    min-height: 100vh;
    background-color: $site-background-color;

    &.#{ $is-cutting } {
        cursor: move;
    }

    &.#{ $is-copying } {
        cursor: copy;
    }

    .#{ $is-preview } & {
        width: 100%;
        display: flex;
        justify-content: center;
    }

    .#{ $is-preview }.#{ $is-tablet } &,
    .#{ $is-preview }.#{ $is-mobile } & {
        align-items: center;
    }

    &__content {
        display: flex;
        justify-content: center;
        position: relative;
        transition: all 0.33s;
        margin-left: auto;
        margin-right: auto;
        width: 100%;
    }

    &__content-container {
        flex-grow: 1;
        background-color: $page-background-color;
        max-width: 100%;

        .#{ $is-preview } & {
            max-width: $max-site-width;
        }

        .#{ $is-preview }.#{ $is-tablet } &,
        .#{ $is-preview }.#{ $is-mobile } & {
            position: absolute;
            overflow: auto;
            top: 50%;
            transform: translateY(-50%);
        }

        .#{ $is-preview }.#{ $is-tablet } & {
            margin-top: -5px;
        }

        .#{ $is-preview }.#{ $is-mobile } & {
            margin-left: 1px;
        }
    }
}
</style>
